// dashboard/src/pages/apps-setup/AppDomainPanel.tsx
// The app's own domain on the Build tab: bought in Cloudflare Registrar at the
// first build (or here with "Buy now", also for an app built before it had
// one), then attached to the API worker; the app's tracker links move onto it
// (old links redirect), the build hands it to the SDK, and the app's privacy
// policy is served on it. Below it, the contact email and the
// privacy policy, which any app can set here, AdMob import or not. A repo
// type that sends no domain (Repos Setup tab) shows only the policy.
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '../../api/client';
import {
  getAppDomain, buyAppDomain, retryAppDomain, saveAppPrivacyPolicy,
  type AppDomainState, type AppDomainStatus,
} from '../../api/app-builds';

const STEP_LABELS: Record<AppDomainState, string> = {
  new: 'Not bought yet',
  registering: 'Registering with Cloudflare…',
  registered: 'Registered — attaching to the API…',
  zone: 'Registered — attaching to the API…',
  attached: 'Attached — moving tracker links…',
  linked: 'Waiting for the HTTPS certificate…',
  ready: 'Ready',
};

function money(amount: string | null, currency: string | null): string {
  if (!amount) return '';
  return currency && currency !== 'USD' ? `${amount} ${currency}` : `$${amount}`;
}

export function AppDomainPanel({ appId, domainOff }: { appId: string; domainOff: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['app-domain', appId],
    queryFn: () => getAppDomain(appId),
    // The cron walks the free steps after registration minute by minute.
    refetchInterval: (query) => {
      const d = query.state.data?.domain;
      return d && d.state !== 'ready' && d.state !== 'new' && !d.error ? 15_000 : false;
    },
  });
  const onDone = (data: AppDomainStatus) => qc.setQueryData(['app-domain', appId], data);
  const buy = useMutation({ mutationFn: () => buyAppDomain(appId), onSuccess: onDone, onSettled: () => q.refetch() });
  const retry = useMutation({ mutationFn: () => retryAppDomain(appId), onSuccess: onDone, onSettled: () => q.refetch() });
  const busy = buy.isPending || retry.isPending;
  const err = (buy.error || retry.error) as ApiError | null;

  const s = q.data;
  const d = s?.domain;
  return (
    <>
      {!domainOff && <section className="bg-surface border border-border rounded-lg p-4 space-y-2">
        <h3 className="font-medium text-content">Domain</h3>
        {q.isLoading ? (
          <p className="text-content-subtle text-sm">Loading…</p>
        ) : d?.hostname ? (
          <>
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <a href={`https://${d.hostname}/health`} target="_blank" rel="noreferrer" className="font-mono text-sm text-primary hover:underline">
                {d.hostname}
              </a>
              <span className={`text-xs ${d.state === 'ready' ? 'text-green-500' : 'text-content-muted'}`}>
                {d.error ? 'Stopped' : STEP_LABELS[d.state]}
              </span>
            </div>
            <p className="text-xs text-content-subtle">
              {[
                d.price && `${money(d.price, d.currency)} / year`,
                d.expires_at && `expires ${d.expires_at.slice(0, 10)}`,
                d.auto_renew === 1 ? 'auto-renew on' : d.auto_renew === 0 ? 'auto-renew off' : null,
              ].filter(Boolean).join(' · ')}
            </p>
            <p className="text-xs text-content-subtle">
              The SDK API host of every build from now on; this app&apos;s tracker links and privacy policy live here too.
            </p>
          </>
      ) : s?.unavailable ? (
        <p className="text-sm text-content-muted">Domain purchase is off: {s.unavailable}. Builds use the shared host.</p>
      ) : (
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-sm text-content-muted">
            {s?.built_before
              ? 'This app was built before it had a domain and uses the shared host. Buying one moves its tracker links onto it (old links keep working, they redirect), and the next build hands it to the SDK; installed builds stay on the shared host.'
              : 'A domain is bought automatically when you start the first build, at the cheapest available extension.'}
          </p>
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              const text = s?.built_before
                ? 'Buy a domain for this already built app? Its tracker links move onto the domain, old links redirect there. The purchase is charged to the Cloudflare account and cannot be refunded.'
                : 'Buy a domain for this app now? The purchase is charged to the Cloudflare account and cannot be refunded.';
              if (confirm(text)) buy.mutate();
            }}
            className="px-3 py-1.5 text-sm rounded-md border border-border text-content hover:bg-surface-muted disabled:opacity-40"
          >
            {buy.isPending ? 'Buying…' : 'Buy now'}
          </button>
        </div>
      )}
      {d?.error && (
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-red-500 text-sm">{d.error}</p>
          <button
            type="button"
            disabled={busy}
            onClick={() => retry.mutate()}
            className="px-3 py-1.5 text-sm rounded-md border border-border text-content hover:bg-surface-muted disabled:opacity-40"
          >
            {retry.isPending ? 'Retrying…' : 'Retry'}
          </button>
        </div>
      )}
      {err && <p className="text-red-500 text-sm">{err.message}</p>}
    </section>}
    {s && <PrivacyPolicySection appId={appId} status={s} domainOff={domainOff} onSaved={onDone} />}
    </>
  );
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * The contact email the policy page shows, and the policy link Play needs.
 * Saved on the app's AdMob import when it has one, else on the app. A
 * never-built app without a domain buys it on save, so the policy lands there.
 */
function PrivacyPolicySection({ appId, status: s, domainOff, onSaved }: {
  appId: string; status: AppDomainStatus; domainOff: boolean; onSaved: (data: AppDomainStatus) => void;
}) {
  const [email, setEmail] = useState(s.contact_email ?? '');
  useEffect(() => { setEmail(s.contact_email ?? ''); }, [s.contact_email]);
  const save = useMutation({ mutationFn: () => saveAppPrivacyPolicy(appId, email.trim()), onSuccess: onSaved });

  const d = s.domain;
  const buys = !d?.hostname && !s.unavailable && !s.built_before && !domainOff;
  const valid = EMAIL_RE.test(email.trim());
  const changed = email.trim() !== (s.contact_email ?? '');
  const onDomain = !!(d?.hostname && s.privacy_policy_url?.startsWith(`https://${d.hostname}/`));

  const submit = () => {
    if (buys && !confirm('This app has no domain yet: saving buys one, so the policy lives on it. The purchase is charged to the Cloudflare account and cannot be refunded. Continue?')) return;
    save.mutate();
  };

  return (
    <section className="bg-surface border border-border rounded-lg p-4 space-y-2">
      <h3 className="font-medium text-content">Privacy policy</h3>
      <p className="text-xs text-content-subtle">
        Google Play needs a policy link; the page shows the app name and this contact email.
        {d?.hostname ? ` It is served on ${d.hostname}.` : ' Without a domain it goes on the shared policy generator.'}
      </p>
      <div className="flex flex-wrap items-end gap-2">
        <label className="flex flex-col gap-1 min-w-[16rem] flex-1">
          <span className="text-content-subtle text-xs">Contact email</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="support@example.com"
            className="px-2 py-1.5 text-sm rounded-md border border-border bg-surface-muted text-content"
          />
        </label>
        <button
          type="button"
          disabled={!valid || save.isPending || (!changed && !!s.privacy_policy_url)}
          onClick={submit}
          className="px-3 py-1.5 text-sm rounded-md border border-border text-content hover:bg-surface-muted disabled:opacity-40"
        >
          {save.isPending ? 'Saving…' : s.privacy_policy_url ? 'Save' : buys ? 'Buy domain & create policy' : 'Create policy'}
        </button>
      </div>
      {s.privacy_policy_url ? (
        <p className="text-xs text-content-subtle">
          Policy:{' '}
          <a href={s.privacy_policy_url} target="_blank" rel="noreferrer" className="font-mono text-primary hover:underline break-all">
            {s.privacy_policy_url}
          </a>
          {d?.hostname && !onDomain && ' (moves to this app\'s domain once it is registered)'}
        </p>
      ) : (
        <p className="text-xs text-content-muted">No privacy policy yet.</p>
      )}
      {save.error && <p className="text-red-500 text-sm">{(save.error as ApiError).message}</p>}
    </section>
  );
}
